import { Column, StatusChip } from '@elementary/common-ui/components';
import dateRenderer from '@elementary/common-ui/components/cellRenderers/dateRenderer';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import dateComparer from '@elementary/common-ui/utils/comparers/dateComparer';
import { Typography } from '@mui/material';
import { formatRelativeTime } from '@elementary/common-ui/utils/datetime';
import RemoveRowButton from '../RuntimeDetails/RemoveRowButton';
import { RuntimeToken } from '../types';
import useRuntime from './useRuntime';

const useRuntimeTokensColumns = (runtimeId: string) => {
  const hasAccountPermission = useAccountPermission();
  const { revokeToken, isRevokingToken } = useRuntime(runtimeId);

  const columns: Column<RuntimeToken>[] = [
    {
      id: 'tokenPrefix',
      header: 'Token',
      filterable: false,
      sortable: false,
      renderer: (token) => (
        <Typography sx={{ fontFamily: "'Geist Mono', monospace" }}>
          {token.tokenPrefix}…
        </Typography>
      ),
    },
    {
      id: 'createdAt',
      width: 120,
      header: 'Created',
      filterable: false,
      renderer: (token) => dateRenderer(token.createdAt, 'L'),
      compareFunc: (a, b) => dateComparer(a.createdAt, b.createdAt),
    },
    {
      id: 'expiresAt',
      width: 120,
      header: 'Expires',
      filterable: false,
      showValueWhenEmpty: true,
      renderer: (token) =>
        token.expiresAt ? (
          dateRenderer(token.expiresAt, 'L')
        ) : (
          <Typography>Never</Typography>
        ),
      compareFunc: (a, b) => dateComparer(a.expiresAt, b.expiresAt),
    },
    {
      id: 'lastUsedAt',
      width: 150,
      header: 'Last used',
      filterable: false,
      showValueWhenEmpty: true,
      renderer: (token) => (
        <Typography>{formatRelativeTime(token.lastUsedAt)}</Typography>
      ),
      compareFunc: (a, b) => dateComparer(a.lastUsedAt, b.lastUsedAt),
    },
    {
      id: 'status',
      width: 110,
      header: 'Status',
      sticky: 'right',
      filterable: false,
      renderer: (token) => <StatusChip status={token.status} />,
    },
  ];

  const revokeColumn: Column<RuntimeToken> = {
    id: 'revoke',
    sticky: 'right',
    width: 64,
    header: '',
    renderer: (token) =>
      token.status === 'active' ? (
        <RemoveRowButton
          tooltip="Revoke token"
          confirmTitle="Revoke token"
          confirmMessage={`Runtime instances using ${token.tokenPrefix}… will stop connecting to Elementary right away. This can't be undone.`}
          confirmText="Revoke"
          onConfirm={() => revokeToken(token.id)}
          isLoading={isRevokingToken}
        />
      ) : (
        <></>
      ),
    filterable: false,
    sortable: false,
    metaColumn: true,
  };

  return hasAccountPermission('EDIT_RUNTIMES')
    ? [...columns, revokeColumn]
    : columns;
};

export default useRuntimeTokensColumns;
